import DialogComponent from "@components/shared/Dialog";
import { Button, ButtonProps } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { useAuth } from "@hooks/useAuth";
import { useDialog } from "@hooks/useDialog";
import useGzLog from "@hooks/useGzLog";
import { useMusicControl } from "@hooks/useMusicControl";
import { usePlans } from "@hooks/usePlans";
import { useScreen } from "@hooks/useScreen";
import { ScreenWithPlayingInfo, useScreens } from "@hooks/useScreens";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@utils/index";
import { useMusicZone } from "@utils/queries";
import { supabase } from "@utils/serverClient";
import { Loader2, Trash2 } from "lucide-react";
import { useRef, useState } from "react";

interface DeleteMusicIntegrationButtonProps extends ButtonProps {
  screenId: string;
  zoneId: number | null;
  onSuccess: () => void;
  onMusicDisabled: (screenId: string) => void;
  children?: React.ReactNode;
}

function DeleteConfirmation({
  screenId,
  zoneId,
  onSuccess,
  onMusicDisabled,
  closeDialog: _closeDialog,
}: DeleteMusicIntegrationButtonProps & { closeDialog: () => void }) {
  const { log } = useGzLog();
  const { screens } = useScreens()
  const screen = screens?.find(s => s.id === screenId)
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const { showErrorSnackbar, showSuccessSnackbar } = useDialog()
  const { mutateAsync: deleteMusicZone, isPending } = useMusicZone.delete();
  const queryClient = useQueryClient();
  const deleteTimeoutRef = useRef<NodeJS.Timeout>();
  const { controlInfo } = useMusicControl(screenId, screen?.location_id ?? undefined)
  const { organization } = useAuth()
  function closeDialog() {
    // clearTimeout(deleteTimeoutRef.current);
    setConfirmText("");
    setError("");
    _closeDialog()
  }

  return (
    <div className="flex flex-col gap-4 pt-4">
      <Input
        type="text"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder="Type DELETE to confirm"
        className="border p-2 rounded"
      />
      <p className="text-red-500">{error}</p>
      <div className="flex flex-row gap-2 items-end">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => closeDialog()}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          className="flex-1"
          disabled={isPending}
          onClick={() => {
            if (confirmText !== "DELETE") {
              return setError("Please type DELETE to confirm");
            }

            try {
              const screens = queryClient.getQueryData(["screens", organization?.id]) as ScreenWithPlayingInfo[] | undefined;
              if (!screens) throw new Error("No screens data available");

              const currentScreen = screens.find(s => s.id === screenId);
              if (!currentScreen?.location_id) throw new Error("No location associated with this screen");

              const isController = controlInfo?.is_owner;

              const affectedScreens = isController ?
                screens.filter(s => s.location_id === currentScreen.location_id && !!s.group_id) :
                [currentScreen];

              // Update local state immediately
              queryClient.invalidateQueries({ queryKey: ["music-zone-count"], exact: false });
              queryClient.setQueryData(["screens", organization?.id], (oldScreens: ScreenWithPlayingInfo[] | undefined) => {
                return oldScreens?.map(s => {
                  if (affectedScreens.some(affected => affected.id === s.id)) {
                    // All affected screens leave the group.
                    const ungrouped = {
                      ...s,
                      group_id: null,
                      playlist_id: null,
                      music_playing: false,
                      last_music_id: null,
                      group_music_infos: null,
                    }
                    // The screen the user removed integration from is fully cleared so a
                    // later re-add starts from a clean slate (no stale zone_id/music_infos).
                    if (s.id === currentScreen.id) {
                      return {
                        ...ungrouped,
                        music_integration_enabled: false,
                        music_active: false,
                        music_status: null,
                        zone_id: null,
                        music_infos: null,
                      }
                    }
                    return ungrouped
                  }
                  return s
                })
              })

              // Schedule actual deletion after 5 seconds
              deleteTimeoutRef.current = setTimeout(async () => {
                if (zoneId) {
                  onMusicDisabled(screenId);
                  // Delete music control records for all affected screens
                  await supabase
                    .from('music_control')
                    .delete()
                    .in('screen_id', affectedScreens.map(s => s.id))
                    .eq('location_id', currentScreen.location_id ?? "");

                  // Every affected screen leaves the group.
                  await supabase.from('screen')
                    .update({ group_id: null, playlist_id: null, music_playing: false, last_music_id: null, group_music_infos: null })
                    .in('id', affectedScreens.map(s => s.id))
                    .eq('location_id', currentScreen.location_id ?? "")

                  // Fully clear the screen the user removed integration from so the DB
                  // state matches CustomChannels after the zone is removed, and a later
                  // re-add starts clean (zone_id null -> new zone created; no stale URL).
                  await supabase.from('screen')
                    .update({
                      music_integration_enabled: false,
                      music_active: false,
                      music_status: null,
                      zone_id: null,
                      music_infos: null,
                      group_id: null,
                      playlist_id: null,
                      music_playing: false,
                      last_music_id: null,
                      group_music_infos: null,
                    })
                    .eq('id', currentScreen.id)

                  await deleteMusicZone({ id: zoneId });
                }
                onSuccess();
              }, 5000);

              // Show success toast with undo button
              const message = isController ?
                "Music integration will be removed for all screens in this location" :
                "Music integration will be removed";
              showSuccessSnackbar(
                () => <DeletedMessageUndo timeout={deleteTimeoutRef.current} message={message} />,
                {
                  duration: 5000,
                }
              );
            } catch (error: any) {
              showErrorSnackbar("Failed to remove music integration");
              if (zoneId)
                log({
                  eventtype: "MusicIntegrationDeleteFailed",
                  name: "MusicIntegrationDeleteFailed",
                  payload: {
                    screenId,
                    zoneId: zoneId,
                    error: error.message,
                  },
                });
            } finally {
              closeDialog();
            }
          }}
        >
          {
            isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          }
          Yes, I am sure
        </Button>
      </div>
    </div>
  )
}

function DeletedMessageUndo({ timeout, message }: { timeout: NodeJS.Timeout | undefined; message: string }) {
  const [undoClicked, setUndoClicked] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessSnackbar } = useDialog();
  return <div className="flex flex-row gap-2 items-center justify-center">
    <h1>{message}</h1>
    {
      !undoClicked && <Button
        variant="secondary"
        onClick={() => {
          setUndoClicked(true);
          clearTimeout(timeout);
          // Revert local state
          queryClient.invalidateQueries({ queryKey: ["screens"] });
          queryClient.invalidateQueries({ queryKey: ["music-zone-count"] });
          showSuccessSnackbar("Music integration removal cancelled");
        }}
      >Undo</Button>
    }
  </div>
}

export function DeleteMusicIntegrationImpl(props: Omit<DeleteMusicIntegrationButtonProps, "screenId" | "zoneId" | "onSuccess" | "onMusicDisabled">) {
  const { emitEvent, getScreens } = useScreens()
  const { screenInfo } = useScreen()
  const screen = screenInfo
  if (!screen?.id || !screen?.zone_id) {
    return null
  }

  return <DeleteMusicIntegrationButton
    {...props}
    screenId={screen.id}
    zoneId={screen.zone_id}
    onSuccess={getScreens}
    onMusicDisabled={(screenId) => {
      emitEvent("musicDisabled", screenId)
    }}
  />
}

export function DeleteMusicIntegrationButton(props: DeleteMusicIntegrationButtonProps) {
  const [open, setOpen] = useState(false);
  const { getMusicCost } = usePlans();
  const { closeDialog } = useDialog()

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true)
  };

  const baseCost = getMusicCost();

  return (
    <>
      <Button
        variant="ghost"
        className={cn(
          "overflow-hidden rounded-md",
          props.className,
        )}
        onClick={handleClick}
        {...props}
      >
        {props.children ?? <Trash2 className="h-4 w-4 text-destructive" />}
      </Button>
      <DialogComponent isDialogOpen={open} closeDialog={() => setOpen(false)} dialogInfo={{
        title: "Remove Music Integration",
        description: baseCost > 0
          ? `Are you sure you want to remove music integration from this screen? While you'll still be billed for this month's usage, you won't be charged the $${baseCost}/month fee going forward.\n\nType DELETE to confirm:`
          : "Are you sure you want to remove music integration from this screen?\n\nType DELETE to confirm:",
        customContent: <DeleteConfirmation {...props} closeDialog={() => {
          closeDialog()
          setOpen(false)
        }} />,
      }} />
    </>
  );
}